










































































































































































































































































import { PropType, VNode } from 'vue';
import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';
import {
  EmployeeFormSubmissionComment,
  EmployeeFormSubmissionCommentAttachment,
  IAttachment,
  IEmployeeFormFieldSubmission,
  IEmployeeFormSubmissionComment,
  IUser
} from '@/models';
import { ErrorManager, ISystemError } from '@/models/error';
import auth from '@/mixins/auth';
import mixins from '@/utils/mixins';
import snackbar from '@/mixins/snackbar';
import {
  createFile,
  diffDeep,
  getInitials,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';

import VNodes from '../computation/VNodes.vue';
import MentionProvider from '../mention/MentionProvider.vue';
import QuillEditor from '../common/QuillEditor.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';

export default mixins(auth, snackbar).extend({
  name: 'EmployeeFormSubmissionCommentListItem',

  components: {
    DeleteConfirmationDialog,
    VNodes,
    MentionProvider,
    QuillEditor
  },

  props: {
    employeeFormSubmission: {
      type: Object as PropType<IEmployeeFormFieldSubmission>,
      required: true
    },

    employeeFormSubmissionComment: {
      type: Object as () => IEmployeeFormSubmissionComment,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      isEditing: false,

      commentLoading: false,
      commentErrorMessage: '',
      commentDetailErrorMessage: '',

      // delete comment
      deleteCommentErrorMessage: '',
      commentDeleteInProgress: false,
      selectedComment: cloneDeep(this.employeeFormSubmissionComment),
      deleteCommentDialog: false,

      commentTextHtml: this.employeeFormSubmissionComment.getTextHtml(),
      mentionsLoading: false,

      deleteAttachmentDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IAttachment,
      attachments: [] as IAttachment[]
    };
  },

  computed: {
    mentionableUsers: get<IUser[]>(
      'employeeForm/getEmployeeFormSubmissionMentionableUsers'
    ),

    isCommentOwner(): boolean {
      return (
        this.getCurrentUserFullName === this.employeeFormSubmissionComment.owner
      );
    }
  },

  methods: {
    ...mapActions({
      updateEmployeeFormSubmissionComment:
        'employeeForm/updateEmployeeFormSubmissionComment',
      deleteEmployeeFormSubmissionComment:
        'employeeForm/deleteEmployeeFormSubmissionComment',
      fetchEmployeeFormSubmissionCommentsMentionableUsers:
        'employeeForm/fetchEmployeeFormSubmissionCommentsMentionableUsers',
      removeEmployeeFormSubmissionCommentAttachment:
        'employeeForm/removeEmployeeFormSubmissionCommentAttachment',
      addEmployeeFormSubmissionCommentAttachments:
        'employeeForm/addEmployeeFormSubmissionCommentAttachment'
    }),

    getInitials,

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    textHasDifference(): boolean {
      return (
        this.commentTextHtml ===
        this.employeeFormSubmissionComment.getTextHtml()
      );
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedComment.text) && !this.commentLoading
      );
    },

    toggleEditingComment() {
      if (!this.isEditing) {
        // To reset the selected employeeFormSubmission comment data to the original when rediting
        this.selectedComment = cloneDeep(this.employeeFormSubmissionComment);
        this.commentTextHtml = this.employeeFormSubmissionComment.getTextHtml();
      }
      this.isEditing = !this.isEditing;
    },

    stopEditingComment() {
      if (!this.selectedComment.text || this.textHasDifference()) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.deleteCommentErrorMessage = '';
      this.commentErrorMessage = '';
      this.commentDetailErrorMessage = '';
    },

    cancelCommentDeleteDialog() {
      this.deleteCommentDialog = false;
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new EmployeeFormSubmissionCommentAttachment({ file: file })
        )
      );
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchEmployeeFormSubmissionCommentsMentionableUsers(
          this.employeeFormSubmission.id
        );
      } catch (error) {
        this.commentErrorMessage = ErrorManager.extractApiError(
          error as AxiosError
        );
      } finally {
        this.mentionsLoading = false;
      }
    },

    openAttachmentDeleteDialog(attachment: IAttachment) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.deleteAttachmentDialog = true;
    },

    cancelAttachmentDeleteDialog() {
      this.deleteAttachmentDialog = false;
    },

    removeAttachment(attachment: IAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removeEmployeeFormSubmissionCommentAttachment({
        submissionId: this.employeeFormSubmission.id,
        commentId: this.employeeFormSubmissionComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.cancelAttachmentDeleteDialog();
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    },

    removeComment() {
      this.clearAllErrors();
      this.commentDeleteInProgress = true;
      this.deleteEmployeeFormSubmissionComment({
        submissionId: this.employeeFormSubmission.id,
        commentId: this.employeeFormSubmissionComment.id
      })
        .then(() => {
          this.deleteCommentDialog = false;
        })
        .catch(error => {
          this.deleteCommentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentDeleteInProgress = false;
        });
    },

    editComment() {
      this.clearAllErrors();

      const parsedCommentMentionText = EmployeeFormSubmissionComment.parseHtmlToMentionText(
        this.commentTextHtml
      );

      this.selectedComment.text = parsedCommentMentionText;

      const commentDelta = diffDeep(
        this.selectedComment,
        this.employeeFormSubmissionComment,
        true
      );

      this.commentLoading = true;
      return this.updateEmployeeFormSubmissionComment({
        submissionId: this.employeeFormSubmission.id,
        comment: commentDelta
      })
        .then(async () => {
          if (this.attachments.length !== 0) {
            await this.addEmployeeFormSubmissionCommentAttachments({
              submissionId: this.employeeFormSubmission.id,
              commentId: commentDelta.id,
              attachments: this.attachments
            });
            this.attachments = [];
          }
          this.isEditing = false;
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail) {
              this.commentDetailErrorMessage = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    downloadAttachmentFile(attachment: IAttachment) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    }
  }
});
